// src/context/SettingsContext.js
import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '../config/apiconfig';

const SettingsContext = createContext();

export const useSettings = () => useContext(SettingsContext);

export const SettingsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Tax settings state
  const [taxSettings, setTaxSettings] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxError, setTaxError] = useState(null);

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));

  // Function to get tax settings
  const getTaxSettings = async () => {
    setTaxLoading(true);
    setTaxError(null);

    try {
      const res = await axios.get(`${BASE_URL}/settings`);

      if (res.data.status === '200' && res.data.data) {
        // Find the GST/tax setting from the response
        const gstSetting = res.data.data.find(setting =>
          setting.title === 'GST' && setting.status === 'Active'
        );

        if (gstSetting) {
          const taxRate = parseFloat(gstSetting.tax) || 0;
          setTaxSettings({
            id: gstSetting.id,
            title: gstSetting.title,
            taxRate: taxRate,
            taxPercentage: taxRate / 100, // Convert to decimal for calculations
            status: gstSetting.status,
            lastUpdated: gstSetting.update_audit_time || gstSetting.create_audit_time
          });
          return {
            success: true,
            taxRate: taxRate,
            taxPercentage: taxRate / 100
          };
        } else {
          // No active GST setting found, use default
          const defaultTaxRate = 18;
          setTaxSettings({
            id: null,
            title: 'Default GST',
            taxRate: defaultTaxRate,
            taxPercentage: defaultTaxRate / 100,
            status: 'Default',
            lastUpdated: new Date().toISOString()
          });
          console.warn('No active GST setting found, using default 8%');
          return {
            success: true,
            taxRate: defaultTaxRate,
            taxPercentage: defaultTaxRate / 100
          };
        }
      } else {
        throw new Error('Invalid response format');
      }

    } catch (err) {
      console.error('Error fetching tax settings:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch tax settings';
      setTaxError(errorMessage);

      // Fallback to default tax rate on error
      const defaultTaxRate = 8;
      setTaxSettings({
        id: null,
        title: 'Default GST (Fallback)',
        taxRate: defaultTaxRate,
        taxPercentage: defaultTaxRate / 100,
        status: 'Fallback',
        lastUpdated: new Date().toISOString()
      });

      return {
        success: false,
        error: errorMessage,
        taxRate: defaultTaxRate,
        taxPercentage: defaultTaxRate / 100
      };
    } finally {
      setTaxLoading(false);
    }
  };

  // Function to refresh tax settings
  const refreshTaxSettings = async () => {
    return await getTaxSettings();
  };

  // Function to get current tax rate (with fallback)
  const getCurrentTaxRate = () => {
    if (taxSettings) {
      return {
        rate: taxSettings.taxRate,
        percentage: taxSettings.taxPercentage,
        title: taxSettings.title
      };
    }
    // Default fallback
    return {
      rate: 8,
      percentage: 0.08,
      title: 'Default GST'
    };
  };

  // Function to update password
  const updatePassword = async ({ old_password, new_password, update_audit_id }) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token'); // Get token from localStorage
      if (!token) {
        setError('Authentication required. Please login again.');
        setLoading(false);
        return;
      }

      const user = JSON.parse(localStorage.getItem('user'));
      const userId = user?.id;
      if (!userId) {
        setError('User not found. Please login again.');
        setLoading(false);
        return;
      }

      const res = await axios.post(
        `${BASE_URL}/profile/${userId}/update-password`,
        { old_password, new_password, update_audit_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (res.data.status === '200') {
        setSuccess(res.data.message || 'Password updated successfully');
      } else {
        setError(res.data.message || 'Failed to update password');
      }

    } catch (err) {
      console.error(err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to update password.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Submit report API
  const submitReport = async (reportData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Authentication required.');

        // Get user ID from localStorage
        const user = JSON.parse(localStorage.getItem("user"));
        const userId = user?.id;
        if (!userId) throw new Error("User not found. Please login again.");

        // Create FormData
        const formData = new FormData();

        // Append user_id first
        formData.append('user_id', userId);
        formData.append('create_audit_id', userId);

        // Append the rest of the fields
        for (let key in reportData) {
          if (key === 'attachments') {
              reportData.attachments.forEach(file => {
                  formData.append('attachment', file); // use 'attachment' not 'attachments[]'
              });
          } else {
              formData.append(key, reportData[key]);
          }
        }

        const res = await axios.post(`${BASE_URL}/reports`, formData, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        }
        });

        if (res.data.status === '200') {
        setSuccess(res.data.message || 'Report submitted successfully');
        return res.data;
        } else {
        setError(res.data.message || 'Failed to submit report');
        throw new Error(res.data.message || 'Failed to submit report');
        }

    } catch (err) {
        console.error(err);
        setError(err.response?.data?.message || err.message || 'Something went wrong');
        throw err;
    } finally {
        setLoading(false);
    }
    };

    // Inside SettingsProvider
    const deleteAccount = async ({ password }) => {
      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Authentication required. Please login again.');

        const user = JSON.parse(localStorage.getItem('user'));
        const userId = user?.id;
        if (!userId) throw new Error('User not found. Please login again.');

        const res = await axios.delete(`${BASE_URL}/users/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          data: { password, confirm_delete: 'DELETE' } // DELETE body requires 'data' in axios
        });

        if (res.data.status === '200') {
          setSuccess(res.data.message || 'Account deleted successfully');
          // Optionally remove user from localStorage after deletion
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setUser(null);
          setToken(null);
          return res.data;
        } else {
          setError(res.data.message || 'Failed to delete account');
          throw new Error(res.data.message || 'Failed to delete account');
        }

      } catch (err) {
        console.error(err);
        setError(err.response?.data?.message || err.message || 'Something went wrong');
        throw err;
      } finally {
        setLoading(false);
      }
    };

  return (
    <SettingsContext.Provider value={{
      updatePassword,
      deleteAccount,
      loading,
      error,
      success,
      submitReport,
      setError,
      setSuccess,
      // Tax settings
      taxSettings,
      taxLoading,
      taxError,
      getTaxSettings,
      refreshTaxSettings,
      getCurrentTaxRate
    }}>
      {children}
    </SettingsContext.Provider>
  );
};
